import { BidPointEditModel, BidPointViewModel, CheckBidCollisionsResultModel } from "api";
import { isCargoBidPointType } from "helpers/bidpoint";
import { bidPointOptionFlags, bidPointOptionFlagsEnum, bidPointTypesEnum, bidStatusesEnum } from "helpers/enums";
import { buildQuery, OdataFilters, queryCollection, queryParams } from "helpers/reqparams";
import { flagVal } from "helpers/system";
import { alert } from "services/alert";
import { alertRequest, body, request } from "services/request";

const resource = "api/bids";
const truckingbids = "api/truckingbids";
const truckingbidrates = "api/truckingbidrates";
const exploitation = "api/exploitation/bids";
const unstableZones = "api/unstableZones";
const regionDryingPeriods = "api/regionDryingPeriods";

export const getRegionDryingPeriodIds = (id: number) =>
    request(`${regionDryingPeriods}/getPeriodIdsForBid?bidId=${id}`, { method: "GET" });
export const calculatePeriodsForPoints = (params: {}) =>
    request(`${regionDryingPeriods}/calculatePeriodsForPoints`, body(params));
export const getTruckingBidForEdit = (id: number) => {
    return request(`${truckingbids}/get/${id}`, { method: "GET" }).then((data) => {
        if (data) {
            data.bidPoints.forEach((point: BidPointViewModel) => {
                if (point.type === bidPointTypesEnum.CustomPoint) {
                    point.customPointTypeId = point.customPointType?.id;
                }
            });
            data.contractId = data.contract?.id;
            data.cargoOwnerDictionaryItemId = data.counterparty?.id;
        }
        return data;
    });
};

export const applyCloseBid = (
    params: { bidPoints: BidPointEditModel[] },
    alertParams: { successAlerts: boolean; noContentAlerts: boolean },
) => {
    const emptyMileagePointVal = flagVal(bidPointOptionFlags, bidPointOptionFlagsEnum.EmptyMileagePoint);
    let loadPointStarted = false;
    params.bidPoints.forEach((item) => {
        if (isCargoBidPointType(item.type)) {
            loadPointStarted = true;
        }
        if (!item.intOptions) {
            item.intOptions = 0;
        }
        if (loadPointStarted) {
            const hasFlag = (item.intOptions & emptyMileagePointVal) === emptyMileagePointVal;
            if (hasFlag) {
                item.intOptions = item.intOptions - emptyMileagePointVal;
            }
        } else {
            item.intOptions = item.intOptions | emptyMileagePointVal;
        }
    });
    return request(`${truckingbids}/apply`, body(params), {
        shouldAlert: true,
        alertParams,
        withoutErrorAlert: true,
        returnErrorText: true,
    });
};

export const getPointPrediction = (id: number, bidPointId: number) =>
    request("api/registryPoints/bids/adjustPointByBid", body({ id, bidPointId })).then((res) => {
        if (!res || !res.isSuccessful) {
            alert({ type: "warning", message: "Не удалось определить точку выгрузки", title: "Ошибка" });
            return null;
        }
        const {
            addressInfo: { address, displayName, location },
            ...rest
        } = res;
        if (!address) {
            return null;
        }
        if (!address.village) {
            address.village = address.hamlet;
        }
        if (!address.city) {
            address.city = address.town;
        }
        return {
            addressInfo: {
                location,
                address: displayName,
                ...address,
            },
            ...rest,
        };
    });

export const getBidPointVisits = (bidPointId: number) => {
    const query = buildQuery({ filter: { bidPointId } });
    return request(`${truckingbids}/getBidPointVisits${query}`, { method: "GET" });
};

export const closeBidsList = ({ preparedFilter, queryFilter, ...params }: OdataFilters) => {
    const qp = new URLSearchParams(queryFilter as URLSearchParams);
    const query = queryCollection(params, { filter: preparedFilter }).substr(1);
    const queryString = [qp.toString(), query].filter((item) => item.length).join("&");
    return request(`${resource}/getlist?${queryString}`, { method: "GET" }, { countInHeaders: true });
};

export const closeBidsCount = ({ preparedFilter, ...params }: OdataFilters) => {
    const query = queryCollection(params, { count: true, filter: preparedFilter });
    return request(`${resource}/getlist${query}`, { method: "GET" });
};

export const getItemIds = (bidId: number) => request(`${truckingbids}/getItemIds?bidId=${bidId}`, { method: "GET" });
export const applyBidPayment = (params: {}) => alertRequest(`${truckingbids}/applyBidPayment`, body(params));
export const applyBidPaymentDocumentsTracking = (params: {}) =>
    alertRequest(`${truckingbids}/applyBidPaymentDocumentsTracking`, body(params));
export const createInvoice = (params: {}) => alertRequest(`${truckingbids}/createInvoice`, body(params));
export const createAcceptanceCertificate = (params: {}) =>
    alertRequest(`${truckingbids}/createAcceptanceCertificate`, body(params));
export const createAcceptanceCertificateInvoice = (params: {}) =>
    alertRequest(`${truckingbids}/createTaxInvoice`, body(params));
export const createDocumentFromTemplate = (params: {}) =>
    alertRequest(`${truckingbids}/createDocumentFromTemplate`, body(params));
export const requestCoordinates = (bidId: number) => request(`${truckingbids}/requestCoordinates`, body({ bidId }));
export const applyForwardingOrder = (params: {}) => alertRequest(`${truckingbids}/applyForwardingOrder`, body(params));
export const deleteForwardingOrder = (params: {}) =>
    alertRequest(`${truckingbids}/deleteForwardingOrder`, body(params));
export const checkForwardingOrderCarHasChanged = (params: {}) =>
    request(`${truckingbids}/checkForwardingOrderCarHasChanged`, body(params));
export const deleteEmulatedSegment = (params: {}) =>
    alertRequest(`${truckingbids}/deleteEmulatedSegment`, body(params));
export const getUnstableZones = (bidId: number) =>
    request(`${unstableZones}/getListForBid/${bidId}`, { method: "GET" });
export const getBidsForParallelPoint = (params: {}) => request(`${truckingbids}/getBidsForParallelPoint`, body(params));
export const updateOdometerMileage = (params: {}) => request(`${truckingbids}/updateOdometerMileage`, body(params));
export const setEstimatedDateAsPlanned = (params: {}) =>
    request(`${truckingbids}/setEstimatedDateAsPlanned`, body(params));
export const getEstimatedDateForPoint = (pointId: number, bidId: number) =>
    request(`${truckingbids}/getEstimatedDateForPoint?id=${pointId}&bidId=${bidId}`, { method: "GET" });
export const checkItemsBeforeParallelPoint = (params: {}) =>
    request(`${truckingbids}/checkItemsBeforeParallelPoint`, body(params));
export const filterTrackerLogs = (params: {}) => request(`${truckingbids}/filterTrackerLogs`, body(params));
export const getBidParticipants = (bidId: number) =>
    request(`${truckingbids}/getBidParticipants?id=${bidId}`, { method: "GET" });
export const getShiftCarRouteForBid = (carId: number, bidId: number) =>
    request(`${truckingbids}/getShiftCarRouteForBid?carId=${carId}&bidId=${bidId}`, { method: "GET" });
export const applyServicePoint = (params: {}) => request(`${exploitation}/applyServicePoint`, body(params));
export const deleteServicePoint = (params: {}) => alertRequest(`${truckingbids}/deleteServicePoint`, body(params));
export const setBidEstimatedLeaveDate = (params: {}) =>
    request(`${truckingbids}/setBidEstimatedLeaveDate`, body(params));
export const clearBidEstimatedLeaveDate = (params: {}) =>
    request(`${truckingbids}/clearBidEstimatedLeaveDate`, body(params));
export const setBidPointsVisits = (params: {}) => request(`${truckingbids}/setBidPointsVisits`, body(params));
export const getInfoForRecalculation = (id: number) =>
    request(`${resource}/getInfoForRecalculation?bidId=${id}`, { method: "GET" });
export const revertBid = (params: {}) => alertRequest(`${truckingbids}/revert`, body(params));
export const cloneBid = (params: {}) => alertRequest(`${truckingbids}/clone`, body(params));
export const approve = (params: {}) => alertRequest(`${truckingbids}/approve`, body(params));
export const disapprove = (params: {}) => alertRequest(`${truckingbids}/disapprove`, body(params));
export const forceComplete = (params: {}) => alertRequest(`${truckingbids}/forceComplete`, body(params));
export const setBidPointDates = (params: {}) => alertRequest(`${truckingbids}/setBidPointDates`, body(params));
export const patchCloseBid = (params: {}, alertParams?: { successAlerts: boolean; noContentAlerts: boolean }) =>
    alertRequest(`${truckingbids}/patch`, body(params), alertParams);
export const getCloseBidDriver = (params: { phoneNumber: string }) =>
    request(`${truckingbids}/getDriver?phoneNumber=${encodeURIComponent(params.phoneNumber)}`, { method: "GET" });
export const getCloseBidTrailer = (params: { number: string }) =>
    request(`${truckingbids}/getTrailer?number=${params.number}`, { method: "GET" });
export const getCloseBidTruck = (params: { number: string }) =>
    request(`${truckingbids}/getCar?number=${params.number}`, { method: "GET" });
export const setOffer = (params: {}) => alertRequest(`${truckingbidrates}/set`, body(params));
export const getLatestRate = (id: number) =>
    request(`${truckingbidrates}/getLatestRateForBid/${id}`, { method: "GET" });
export const startBid = (bidId: number) =>
    alertRequest(`${truckingbids}/setStatus`, body({ bidId, status: bidStatusesEnum.Started }));
export const setStatus = (bidId: number, status: keyof typeof bidStatusesEnum) =>
    request(`${truckingbids}/SetStatus`, body({ bidId, status }), { shouldAlert: true });
export const setBidKeyFeaturesReviewStage = (params: { stage: string }) =>
    request(`${truckingbids}/setbidkeyfeaturesreviewstage`, body(params));
export const getExcessMileage = (bidId: number) =>
    request(`${truckingbids}/getExcessMileage/${bidId}`, { method: "GET" });
export const applyExcessMileage = (params: {}) => alertRequest(`${truckingbids}/applyExcessMileage`, body(params));
export const refreshBid = (params: {}) => alertRequest(`${resource}/refresh`, body(params));
export const sendBidsForFactoring = (params: { id: number }[]) =>
    alertRequest(`${truckingbids}/sendBidsForFactoring`, body({ bids: params.map((id) => ({ id })) }));
export const applyItemChangePoints = (params: {}) =>
    alertRequest(`${truckingbids}/applyItemChangePoints`, body(params));
export const deleteItemChangePoint = (params: {}) =>
    alertRequest(`${truckingbids}/deleteItemChangePoint`, body(params));
export const getItemChangePoints = (bidId: number) =>
    request(`${truckingbids}/getItemChangePoints?bidId=${bidId}`, { method: "GET" });
export const setItemChangePointDates = (params: {}) =>
    alertRequest(`${truckingbids}/setItemChangePointDates`, body(params));
/** Checks collisions using the saved bid's vehicles and the supplied dates. */
export const getBidCollisions = (params: {}): Promise<CheckBidCollisionsResultModel> =>
    request(`${truckingbids}/getBidCollisions${queryParams(params)}`, { method: "GET" });
export const getClassificationTypePreview = ({ id, ...params }: { id: number }) =>
    request(`${truckingbids}/getClassificationTypePreview${queryParams({ bidId: id, ...params })}`, { method: "GET" });
export const getAverageRateByDestinationForBid = (params: {
    planEnterDate: string;
    from: [number, number];
    to: [number, number];
    trailerId?: number | string;
}) => {
    const searchParams: string[] = [];

    searchParams.push(`planEnterDate=${encodeURIComponent(params.planEnterDate)}`);
    searchParams.push(`from[0]=${params.from[0]}`);
    searchParams.push(`from[1]=${params.from[1]}`);
    searchParams.push(`to[0]=${params.to[0]}`);
    searchParams.push(`to[1]=${params.to[1]}`);

    if (params.trailerId !== undefined && params.trailerId !== null) {
        searchParams.push(`trailerId=${encodeURIComponent(params.trailerId)}`);
    }

    return request(`${truckingbids}/getAverageRateByDestinationForBid?${searchParams.join("&")}`, { method: "GET" });
};
